/*
 * Komi: interações da página de produto e das seções próprias.
 * Sem biblioteca externa. Tudo respeita prefers-reduced-motion e funciona
 * sem JavaScript (o HTML já sai com o conteúdo final).
 *
 * Elementos:
 *  <komi-revela>      entra suave quando aparece na tela
 *  <komi-contador>    números que contam até o valor ao entrar na tela
 *  <komi-abas>        passos em abas, com troca automática que para ao tocar
 *  <komi-alterna>     cartões que abrem ou viram ao toque (o que vem, sabores)
 *  <komi-comparar>    chave "de sempre" x "roxa"
 *  <komi-planos>      troca entre planos do Clube
 *  <komi-rolagem>     carrossel lateral com setas
 *  <komi-leve-mais>   leve 1, 2 ou 3 com desconto, ligado ao formulário de compra
 */

const semMovimento = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const moeda = (() => {
  const formato = new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
    style: 'currency',
    currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'BRL',
  });
  return (centavos) => formato.format(Math.round(centavos) / 100).replace(/ /g, ' ');
})();

/** Observa uma vez quando o elemento entra na tela. */
function quandoAparecer(elemento, callback, margem = '0px 0px -12% 0px') {
  if (!('IntersectionObserver' in window)) {
    callback();
    return () => {};
  }
  const observador = new IntersectionObserver(
    (entradas) => {
      for (const entrada of entradas) {
        if (entrada.isIntersecting) {
          observador.disconnect();
          callback();
        }
      }
    },
    { rootMargin: margem, threshold: 0.15 }
  );
  observador.observe(elemento);
  return () => observador.disconnect();
}

function definir(nome, classe) {
  if (!customElements.get(nome)) customElements.define(nome, classe);
}

/* ---------- Revela ---------- */

class KomiRevela extends HTMLElement {
  connectedCallback() {
    if (semMovimento() || window.Shopify?.designMode) {
      this.setAttribute('visivel', '');
      return;
    }
    const caixa = this.getBoundingClientRect();
    if (caixa.top < window.innerHeight * 0.9) {
      this.setAttribute('visivel', '');
      return;
    }
    this.setAttribute('esperando', '');
    this.parar = quandoAparecer(this, () => {
      this.removeAttribute('esperando');
      this.setAttribute('visivel', '');
    });
  }

  disconnectedCallback() {
    this.parar?.();
  }
}
definir('komi-revela', KomiRevela);

/* ---------- Contador ---------- */

class KomiContador extends HTMLElement {
  connectedCallback() {
    const numeros = [...this.querySelectorAll('[data-alvo]')];
    if (!numeros.length || semMovimento() || window.Shopify?.designMode) return;
    const caixa = this.getBoundingClientRect();
    if (caixa.top < window.innerHeight * 0.85 && caixa.bottom > 0) return;

    for (const n of numeros) n.textContent = n.dataset.inicio || '0';
    this.parar = quandoAparecer(this, () => this.contar(numeros));
  }

  disconnectedCallback() {
    this.parar?.();
    cancelAnimationFrame(this.quadro);
    clearTimeout(this.garantia);
  }

  /** @param {HTMLElement[]} numeros */
  contar(numeros) {
    const duracao = 1100;
    const inicio = performance.now();
    const passo = (agora) => {
      const t = Math.min(1, (agora - inicio) / duracao);
      const suave = 1 - Math.pow(1 - t, 3);
      for (const n of numeros) {
        const alvo = Number(n.dataset.alvo) || 0;
        const de = Number(n.dataset.inicio) || 0;
        const casas = Number(n.dataset.casas) || 0;
        const valor = de + (alvo - de) * suave;
        n.textContent = casas ? valor.toFixed(casas).replace('.', ',') : String(Math.round(valor));
      }
      if (t < 1) this.quadro = requestAnimationFrame(passo);
    };
    this.quadro = requestAnimationFrame(passo);
    // Garantia: se o navegador não desenhar quadros (aba em segundo plano), o número final aparece mesmo assim
    clearTimeout(this.garantia);
    this.garantia = setTimeout(() => {
      cancelAnimationFrame(this.quadro);
      passo(inicio + duracao);
    }, duracao + 250);
  }
}
definir('komi-contador', KomiContador);

/* ---------- Abas (passos) ---------- */

class KomiAbas extends HTMLElement {
  connectedCallback() {
    this.abas = [...this.querySelectorAll('[role="tab"]')];
    this.paineis = [...this.querySelectorAll('[role="tabpanel"]')];
    this.fotos = [...this.querySelectorAll('[data-foto]')];
    if (!this.abas.length) return;

    this.tempo = Number(this.dataset.tempo) || 5200;
    this.automatico = this.dataset.automatico === 'true' && !semMovimento() && !window.Shopify?.designMode;

    this.aoClicar = (evento) => {
      const aba = evento.target.closest('[role="tab"]');
      if (!aba || !this.contains(aba)) return;
      this.pararAuto();
      this.ativar(this.abas.indexOf(aba), true);
    };
    this.aoTeclar = (evento) => {
      const i = this.abas.indexOf(document.activeElement);
      if (i < 0) return;
      let novo = -1;
      if (evento.key === 'ArrowDown' || evento.key === 'ArrowRight') novo = (i + 1) % this.abas.length;
      if (evento.key === 'ArrowUp' || evento.key === 'ArrowLeft') novo = (i - 1 + this.abas.length) % this.abas.length;
      if (evento.key === 'Home') novo = 0;
      if (evento.key === 'End') novo = this.abas.length - 1;
      if (novo < 0) return;
      evento.preventDefault();
      this.pararAuto();
      this.ativar(novo, true);
    };
    this.addEventListener('click', this.aoClicar);
    this.addEventListener('keydown', this.aoTeclar);

    if (this.automatico) {
      this.addEventListener('pointerenter', () => this.pausar(true));
      this.addEventListener('pointerleave', () => this.pausar(false));
      this.addEventListener('focusin', () => this.pausar(true));
      this.visivel = false;
      if ('IntersectionObserver' in window) {
        this.observador = new IntersectionObserver(
          ([entrada]) => {
            this.visivel = entrada.isIntersecting;
            this.agendar();
          },
          { threshold: 0.35 }
        );
        this.observador.observe(this);
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.aoClicar);
    this.removeEventListener('keydown', this.aoTeclar);
    this.observador?.disconnect();
    clearTimeout(this.relogio);
    cancelAnimationFrame(this.quadro);
  }

  ativar(indice, foco = false) {
    this.abas.forEach((aba, i) => {
      const ativa = i === indice;
      aba.setAttribute('aria-selected', String(ativa));
      aba.tabIndex = ativa ? 0 : -1;
      if (ativa && foco) aba.focus({ preventScroll: true });
    });
    this.paineis.forEach((painel, i) => (painel.hidden = i !== indice));
    this.fotos.forEach((foto, i) => foto.toggleAttribute('ativa', i === indice));
    this.atual = indice;
    this.style.setProperty('--komi-abas-tempo', `${this.tempo}ms`);
    this.agendar();
  }

  pausar(pausado) {
    this.pausado = pausado;
    this.toggleAttribute('pausado', pausado);
    this.agendar();
  }

  pararAuto() {
    this.automatico = false;
    this.removeAttribute('rodando');
    clearTimeout(this.relogio);
  }

  agendar() {
    clearTimeout(this.relogio);
    if (!this.automatico || this.pausado || !this.visivel) {
      this.removeAttribute('rodando');
      return;
    }
    // Reinicia a barra de progresso da aba ativa (dois quadros, sem forçar layout)
    this.removeAttribute('rodando');
    cancelAnimationFrame(this.quadro);
    this.quadro = requestAnimationFrame(() => {
      this.quadro = requestAnimationFrame(() => this.setAttribute('rodando', ''));
    });
    this.relogio = setTimeout(() => {
      const proximo = ((this.atual ?? 0) + 1) % this.abas.length;
      this.ativar(proximo);
    }, this.tempo);
  }
}
definir('komi-abas', KomiAbas);

/* ---------- Cartões que abrem ou viram ---------- */

class KomiAlterna extends HTMLElement {
  connectedCallback() {
    this.aoClicar = (evento) => {
      const botao = evento.target.closest('[data-alterna]');
      if (!botao || !this.contains(botao)) return;
      const cartao = botao.closest('[data-cartao]');
      if (!cartao) return;
      const aberto = cartao.hasAttribute('aberto');
      if (this.dataset.um === 'true' && !aberto) {
        for (const outro of this.querySelectorAll('[data-cartao][aberto]')) this.fechar(outro);
      }
      aberto ? this.fechar(cartao) : this.abrir(cartao);
    };
    this.addEventListener('click', this.aoClicar);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.aoClicar);
  }

  abrir(cartao) {
    cartao.setAttribute('aberto', '');
    for (const b of cartao.querySelectorAll('[data-alterna]')) b.setAttribute('aria-expanded', 'true');
    for (const f of cartao.querySelectorAll('[data-frente]')) f.setAttribute('aria-hidden', 'true');
    for (const v of cartao.querySelectorAll('[data-verso]')) v.removeAttribute('aria-hidden');
  }

  fechar(cartao) {
    cartao.removeAttribute('aberto');
    for (const b of cartao.querySelectorAll('[data-alterna]')) b.setAttribute('aria-expanded', 'false');
    for (const f of cartao.querySelectorAll('[data-frente]')) f.removeAttribute('aria-hidden');
    for (const v of cartao.querySelectorAll('[data-verso]')) v.setAttribute('aria-hidden', 'true');
  }
}
definir('komi-alterna', KomiAlterna);

/* ---------- Comparativo ---------- */

class KomiComparar extends HTMLElement {
  connectedCallback() {
    this.botoes = [...this.querySelectorAll('[data-lado]')];
    this.aoClicar = (evento) => {
      const botao = evento.target.closest('[data-lado]');
      if (!botao || !this.contains(botao)) return;
      this.mostrar(botao.dataset.lado);
    };
    this.addEventListener('click', this.aoClicar);
    this.mostrar(this.getAttribute('lado') || 'roxa');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.aoClicar);
  }

  mostrar(lado) {
    this.setAttribute('lado', lado);
    for (const b of this.botoes) b.setAttribute('aria-pressed', String(b.dataset.lado === lado));
  }
}
definir('komi-comparar', KomiComparar);

/* ---------- Planos do Clube ---------- */

class KomiPlanos extends HTMLElement {
  connectedCallback() {
    this.botoes = [...this.querySelectorAll('[data-plano-botao]')];
    this.planos = [...this.querySelectorAll('[data-plano]')];
    this.aoClicar = (evento) => {
      const botao = evento.target.closest('[data-plano-botao]');
      if (!botao || !this.contains(botao)) return;
      this.escolher(botao.dataset.planoBotao);
    };
    this.addEventListener('click', this.aoClicar);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.aoClicar);
  }

  escolher(id) {
    for (const b of this.botoes) b.setAttribute('aria-pressed', String(b.dataset.planoBotao === id));
    for (const p of this.planos) p.hidden = p.dataset.plano !== id;
  }
}
definir('komi-planos', KomiPlanos);

/* ---------- Rolagem lateral ---------- */

class KomiRolagem extends HTMLElement {
  connectedCallback() {
    this.trilho = this.querySelector('[data-trilho]');
    this.anterior = this.querySelector('[data-anterior]');
    this.proximo = this.querySelector('[data-proximo]');
    this.barra = this.querySelector('[data-progresso]');
    if (!this.trilho) return;

    this.atualizar = () => {
      const max = this.trilho.scrollWidth - this.trilho.clientWidth;
      const pos = Math.abs(this.trilho.scrollLeft);
      if (this.anterior) this.anterior.disabled = pos <= 2;
      if (this.proximo) this.proximo.disabled = pos >= max - 2;
      this.toggleAttribute('sem-rolagem', max <= 2);
      if (this.barra) {
        const visivel = this.trilho.clientWidth / this.trilho.scrollWidth;
        this.barra.style.setProperty('--komi-barra-largura', `${Math.max(visivel, 0.12) * 100}%`);
        this.barra.style.setProperty('--komi-barra-pos', max > 0 ? String(pos / max) : '0');
      }
    };
    this.mover = (direcao) => {
      const cartao = this.trilho.querySelector(':scope > *');
      const largura = cartao ? cartao.getBoundingClientRect().width + 16 : this.trilho.clientWidth * 0.8;
      this.trilho.scrollBy({ left: direcao * largura, behavior: semMovimento() ? 'auto' : 'smooth' });
    };
    this.aoAnterior = () => this.mover(-1);
    this.aoProximo = () => this.mover(1);
    this.anterior?.addEventListener('click', this.aoAnterior);
    this.proximo?.addEventListener('click', this.aoProximo);
    this.trilho.addEventListener('scroll', this.atualizar, { passive: true });
    this.redimensiona = new ResizeObserver(this.atualizar);
    this.redimensiona.observe(this.trilho);
    this.atualizar();
  }

  disconnectedCallback() {
    this.anterior?.removeEventListener('click', this.aoAnterior);
    this.proximo?.removeEventListener('click', this.aoProximo);
    this.trilho?.removeEventListener('scroll', this.atualizar);
    this.redimensiona?.disconnect();
  }
}
definir('komi-rolagem', KomiRolagem);

/* ---------- Leve 1, 2 ou 3 ---------- */

class KomiLeveMais extends HTMLElement {
  connectedCallback() {
    this.secao = this.closest('.shopify-section') || document;
    try {
      this.variantes = JSON.parse(this.querySelector('script[data-variantes]')?.textContent || '[]');
    } catch {
      this.variantes = [];
    }
    this.pcts = { 1: 0, 2: Number(this.dataset.pct2) || 0, 3: Number(this.dataset.pct3) || 0 };
    this.escolhido = Number(this.querySelector('input[type="radio"]:checked')?.value) || 1;

    this.aoMudar = (evento) => {
      const radio = evento.target.closest('input[type="radio"]');
      if (!radio || !this.contains(radio)) return;
      this.escolher(Number(radio.value));
    };
    this.addEventListener('change', this.aoMudar);

    // Quantidade mexida à mão (+ e -): o cartão acompanha
    this.aoQuantidade = (evento) => {
      if (this.aplicando) return;
      const q = Number(evento.detail?.quantity) || Number(this.entrada()?.value) || 1;
      this.marcar(q);
    };
    this.secao.addEventListener('quantity-selector:update', this.aoQuantidade);

    // Troca de variante: recalcula os preços e devolve a quantidade escolhida
    this.aoVariante = (evento) => {
      if (evento.target instanceof Element && evento.target.closest('product-card')) return;
      evento.promise
        ?.then(({ detail }) => {
          const id = String(detail?.resource?.id ?? '');
          const v = this.variantes.find((x) => String(x.id) === id);
          if (v) this.variante = v;
          else if (detail?.resource) {
            this.variante = {
              id,
              price: Number(detail.resource.price) || 0,
              compare_at_price: Number(detail.resource.compare_at_price) || 0,
              available: detail.resource.available !== false,
            };
          }
          // Depois do morph do formulário, que roda na mesma volta
          setTimeout(() => {
            this.precos();
            this.escolher(this.escolhido, false);
          }, 0);
        })
        .catch(() => {});
    };
    this.secao.addEventListener('shopify:product:select', this.aoVariante);
    this.ouvirEventoPadrao();

    this.variante = this.variantes.find((x) => String(x.id) === this.dataset.variante) || this.variantes[0];
    this.precos();
    // Aplica a quantidade inicial depois que o formulário existir
    requestAnimationFrame(() => this.escolher(this.escolhido, false));
  }

  async ouvirEventoPadrao() {
    try {
      const { StandardEvents } = await import('@shopify/events');
      const nome = StandardEvents?.productSelect;
      if (nome && nome !== 'shopify:product:select') {
        this.nomeEvento = nome;
        this.secao.addEventListener(nome, this.aoVariante);
      }
    } catch {
      /* sem eventos padrão: os preços ficam os do carregamento */
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.aoMudar);
    this.secao?.removeEventListener('quantity-selector:update', this.aoQuantidade);
    this.secao?.removeEventListener('shopify:product:select', this.aoVariante);
    if (this.nomeEvento) this.secao?.removeEventListener(this.nomeEvento, this.aoVariante);
  }

  formulario() {
    return this.secao.querySelector('product-form-component');
  }

  entrada() {
    return /** @type {HTMLInputElement|null} */ (this.formulario()?.querySelector('input[name="quantity"]'));
  }

  precos() {
    const v = this.variante;
    if (!v) return;
    const preco = Number(v.price) || 0;
    const de = Number(v.compare_at_price) || 0;
    const base = de > preco ? de : preco;
    for (const cartao of this.querySelectorAll('[data-qtd]')) {
      const q = Number(cartao.dataset.qtd);
      const pct = this.pcts[q] || 0;
      const riscado = base * q;
      const total = Math.round((preco * q * (100 - pct)) / 100);
      const riscadoEl = cartao.querySelector('[data-riscado]');
      const totalEl = cartao.querySelector('[data-total]');
      const cadaEl = cartao.querySelector('[data-cada]');
      const economiaEl = cartao.querySelector('[data-economia]');
      if (totalEl) totalEl.textContent = moeda(total);
      if (riscadoEl) {
        riscadoEl.textContent = moeda(riscado);
        riscadoEl.closest('[data-riscado-linha]')?.toggleAttribute('hidden', riscado <= total);
      }
      if (cadaEl) cadaEl.textContent = moeda(total / q);
      if (economiaEl) {
        economiaEl.textContent = moeda(riscado - total);
        economiaEl.closest('[data-economia-linha]')?.toggleAttribute('hidden', riscado - total < 1);
      }
      cartao.dataset.valor = String(total);
    }
    this.toggleAttribute('indisponivel', v.available === false);
  }

  marcar(q) {
    let achou = false;
    for (const radio of this.querySelectorAll('input[type="radio"]')) {
      const igual = Number(radio.value) === q;
      radio.checked = igual;
      if (igual) achou = true;
    }
    this.toggleAttribute('outra-quantidade', !achou);
    if (achou) this.escolhido = q;
    this.botao(achou ? q : null);
  }

  escolher(q, avisar = true) {
    this.escolhido = q;
    this.marcar(q);
    const entrada = this.entrada();
    if (!entrada) return;
    this.aplicando = true;
    entrada.value = String(q);
    const seletor = entrada.closest('quantity-selector-component');
    // @ts-ignore
    seletor?.updateButtonStates?.();
    // Avisa quem ouve a quantidade (barra de compra fixa, por exemplo)
    // @ts-ignore
    if (seletor?.onQuantityChange) seletor.onQuantityChange();
    else if (avisar) entrada.dispatchEvent(new Event('change', { bubbles: true }));
    this.aplicando = false;
    this.botao(q);
  }

  /** Muda o texto do botão de compra para dizer a quantidade escolhida. */
  botao(q) {
    const botao = this.formulario()?.querySelector('[ref="addToCartButton"]');
    if (!botao) return;
    const texto = [...botao.childNodes].find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
    if (!texto) return;
    const atual = texto.textContent.trim();
    // Guarda o texto que veio do servidor (muda com a variante: comprar, esgotado)
    if (!botao.dataset.komiVarios || atual !== botao.dataset.komiVarios) botao.dataset.komiTextoOriginal = atual;
    const original = botao.dataset.komiTextoOriginal || atual;
    const podeComprar = !botao.disabled && original === this.dataset.textoComprar;
    if (!podeComprar || !q || q < 2) {
      texto.textContent = original;
      return;
    }
    const novo = this.dataset.textoVarios.replace('[q]', String(q));
    botao.dataset.komiVarios = novo;
    texto.textContent = novo;
  }
}
definir('komi-leve-mais', KomiLeveMais);
